import React, { useEffect } from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@fiverr-private/icons';
import { Container, Stack, Wrap } from '@fiverr-private/layout_components';
import { Modal, ModalBody, ModalCloseBtn, ModalContent, ModalFooter, ModalHeader } from '@fiverr-private/overlay';
import { theme } from '@fiverr-private/theme';
import { Heading, Text } from '@fiverr-private/typography';
import { AgencyService } from '../../../types';
import { bigQuery, EVENT_TYPE } from '../../../monitoring/bigQuery';
import { CustomOrderButton } from '../../CustomOrder/CustomOrderButton';
import { PreviewItemIcon } from '../PreviewItemIcon';
import { PreviewItemSubService } from '../PreviewItemSubService';

import { CONTACT_BUTTON_POSITIONS } from '../../../monitoring/bigQuery/createContactButtonEventEnrichment';
import { navButtonClass, platformItemClass } from './styles.ve.css';

interface ItemModalProps {
    item: AgencyService;
    itemIndex: number;
    onNextItem: null | (() => void);
    onPrevItem: null | (() => void);
    onClose: () => void;
    showMoreExist: boolean;
}

export const ItemModal = ({ item, itemIndex, onNextItem, onPrevItem, onClose, showMoreExist }: ItemModalProps) => {
    useEffect(() => {
        bigQuery.impressionEvent(EVENT_TYPE.AGENCY_SERVICE_POPUP_IS_SHOWN, {
            agency: {
                navigation_button_exist: showMoreExist,
                services: [item.serviceName],
                service_position: itemIndex + 1,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePrevItemClick = () => {
        if (!onPrevItem) {
            return;
        }

        onPrevItem();
        bigQuery.clickEvent(EVENT_TYPE.CLICKED_ON_NAVIGATION_IN_AGENCY_PROVIDED_SERVICES_POPUP, {
            agency: {
                services: [item.serviceName],
                service_position: itemIndex + 1,
            },
        });
    };

    const handleNextItemClick = () => {
        if (!onNextItem) {
            return;
        }

        onNextItem();
        bigQuery.clickEvent(EVENT_TYPE.CLICKED_ON_NAVIGATION_IN_AGENCY_PROVIDED_SERVICES_POPUP, {
            agency: {
                services: [item.serviceName],
                service_position: itemIndex + 1,
            },
        });
    };

    return (
        <Modal isOpen onClose={onClose}>
            <ModalContent size="sm">
                <ModalHeader>
                    <I18n k="agency_page_perseus.services_we_provide" />
                    <ModalCloseBtn />
                </ModalHeader>
                <ModalBody>
                    <Container marginTop="2">
                        <PreviewItemIcon itemIndex={itemIndex} itemServiceId={item.serviceId} />
                    </Container>
                    <Heading as="h5" marginBottom="4">
                        {item.serviceName}
                    </Heading>
                    <Stack direction="column" gap="2" marginBottom="7">
                        {item.subServices.map((subService) => (
                            <PreviewItemSubService key={subService.id} name={subService.name} />
                        ))}
                    </Stack>
                    {item.platforms.length > 0 && (
                        <Container marginBottom="7">
                            <Text size="b_md" fontWeight="semibold" marginBottom="2">
                                <I18n k="agency_page_perseus.we_specialize_in" />
                            </Text>
                            <Wrap gap="0">
                                {item.platforms.map((platform) => (
                                    <Text key={platform.id} size="b_md" className={platformItemClass}>
                                        {platform.name}
                                    </Text>
                                ))}
                            </Wrap>
                        </Container>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Stack width="100%" alignItems="center" justifyContent="spaceBetween">
                        <Stack gap="4">
                            <button disabled={!onPrevItem} onClick={handlePrevItemClick} className={navButtonClass}>
                                <ChevronLeftIcon color={theme.colors.grey_900} size={12} />
                            </button>
                            <button disabled={!onNextItem} onClick={handleNextItemClick} className={navButtonClass}>
                                <ChevronRightIcon color={theme.colors.grey_900} size={12} />
                            </button>
                        </Stack>
                        <CustomOrderButton
                            onModalOpen={onClose}
                            biSource={`service modal ${item.serviceName}`}
                            position={CONTACT_BUTTON_POSITIONS.SERVICES_SECTION}
                        />
                    </Stack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
